import React, { useState, useMemo } from "react";
import {
	Box,
	Typography,
	Paper,
	CircularProgress,
	Divider,
	Autocomplete,
	TextField,
	Button,
	Chip,
	Tab,
	Tabs,
	Menu,
	MenuItem
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
	Search as SearchIcon,
	ArrowDropDown as ArrowDropDownIcon,
	Check as CheckIcon,
	RestartAlt as RestartAltIcon
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n/i18n";
import { zoullooColors } from "../../theme";
import {
	ProjectEvaluation,
	ProjectEvaluationStatus,
	EvaluatorType
} from "../../models/ProjectEvaluation";
import {
	useSearchProjectEvaluations,
	useModerateProjectEvaluation,
	useEditProjectEvaluation
} from "../../hooks/useProjectEvaluations";
import { RichTextReadOnly } from "mui-tiptap";
import { getExtensions } from "../../components/UI/HtmlEditor";
import StarRating from "../../components/project/evaluation/StarRating";
import CompanyAutocomplete from "../../components/common/CompanyAutocomplete";
import { Company } from "../../models/Company";
import EvaluationEditDialog from "../../components/project/evaluation/EvaluationEditDialog";
import { useEnvironmentNavigation } from "../../hooks/useEnvironmentNavigation";
import TablePagination, { SortConfig, SortOption } from "../../components/common/pagination/TablePagination";

// ── Types ──────────────────────────────────────────────────────

interface ProjectGroup {
	key: string;
	companyName: string;
	projectNumber: string;
	projectName: string;
	evaluations: ProjectEvaluation[];
}

type SortField = "title" | "date";

// ── Helpers ────────────────────────────────────────────────────

const formatDate = (dateStr?: string): string => {
	if (!dateStr) return "-";
	const d = new Date(dateStr);
	return d.toLocaleDateString(i18n.language === "fr" ? "fr-FR" : "en-GB", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric"
	});
};

const getStatusColor = (status: ProjectEvaluationStatus) => {
	switch (status) {
		case ProjectEvaluationStatus.Submitted:
			return zoullooColors.info.main;
		case ProjectEvaluationStatus.Published:
			return zoullooColors.success.main;
		case ProjectEvaluationStatus.Rejected:
			return zoullooColors.error.main;
		default:
			return zoullooColors.grey[450];
	}
};

// ── Component ──────────────────────────────────────────────────

const Evaluations: React.FC = () => {
	const { t } = useTranslation();
	const { navigateByEnvironment } = useEnvironmentNavigation();

	const ratingOptions = useMemo(() => [
		{ label: t("evaluations.filters.ratingAll"), value: null },
		{ label: "1", value: 1 },
		{ label: "2", value: 2 },
		{ label: "3", value: 3 },
		{ label: "4", value: 4 },
		{ label: "5", value: 5 }
	], [t]);

	const consentOptions = useMemo(() => [
		{ label: t("evaluations.filters.consentAll"), value: null },
		{ label: t("evaluations.filters.consentYes"), value: true },
		{ label: t("evaluations.filters.consentNo"), value: false }
	], [t]);

	const statusOptions = useMemo(() => [
		{ label: t("evaluations.filters.statusAll"), value: null },
		{ label: t("evaluations.status.submitted"), value: ProjectEvaluationStatus.Submitted },
		{ label: t("evaluations.status.published"), value: ProjectEvaluationStatus.Published },
		{ label: t("evaluations.status.rejected"), value: ProjectEvaluationStatus.Rejected }
	], [t]);

	const getStatusLabel = (status: ProjectEvaluationStatus) => {
		switch (status) {
			case ProjectEvaluationStatus.Submitted:
				return t("evaluations.status.submitted");
			case ProjectEvaluationStatus.Published:
				return t("evaluations.status.published");
			case ProjectEvaluationStatus.Rejected:
				return t("evaluations.status.rejected");
			default:
				return status;
		}
	};

	// Tab state (0 = Client evals of talents, 1 = Freelancer evals of projects)
	const [activeTab, setActiveTab] = useState(0);

	// Pagination
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);

	// Filters
	const [filterPersonName, setFilterPersonName] = useState("");
	const [filterProjectName, setFilterProjectName] = useState("");
	const [filterProjectNumber, setFilterProjectNumber] = useState("");
	const [filterCompany, setFilterCompany] = useState<Company | null>(null);
	const [filterRating, setFilterRating] = useState<number | null>(null);
	const [filterConsent, setFilterConsent] = useState<boolean | null>(null);
	const [filterStatus, setFilterStatus] = useState<ProjectEvaluationStatus | null>(null);

	// Applied filters (only applied on search click)
	const [appliedFilters, setAppliedFilters] = useState({
		personName: "",
		projectName: "",
		projectNumber: "",
		companyName: "",
		rating: null as number | null,
		consent: null as boolean | null,
		status: null as ProjectEvaluationStatus | null
	});

	// Sort
	const [sortField, setSortField] = useState<SortField>("date");
	const [currentSort, setCurrentSort] = useState<SortConfig>({ sortField: "date", sortDirection: "Desc" });

	const sortOptions: SortOption[] = useMemo(() => [
		{ label: t("evaluations.sort.title"), value: "title" },
		{ label: t("evaluations.sort.date"), value: "date" }
	], [t]);

	const handleSortChange = (sort: SortConfig) => {
		setCurrentSort(sort);
		if (sort.sortField) {
			setSortField(sort.sortField as SortField);
		}
		setPage(1);
	};

	// Menu anchor for moderation dropdown per evaluation
	const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
	const [menuEvaluation, setMenuEvaluation] = useState<ProjectEvaluation | null>(null);

	// Edit dialog
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [editEvaluation, setEditEvaluation] = useState<ProjectEvaluation | null>(null);
	const [editRating, setEditRating] = useState(0);
	const [editComment, setEditComment] = useState("");

	const evaluatorType = activeTab === 0 ? EvaluatorType.Client : EvaluatorType.Freelancer;

	const { data, isLoading } = useSearchProjectEvaluations(
		page,
		pageSize,
		undefined,
		evaluatorType
	);

	const moderateMutation = useModerateProjectEvaluation();
	const editMutation = useEditProjectEvaluation();

	// ── Client-side filtering ──────────────────────────────────

	const filteredItems = useMemo(() => {
		if (!data?.items) return [];
		let items = [...data.items];

		if (appliedFilters.personName) {
			const search = appliedFilters.personName.toLowerCase();
			items = items.filter(e => {
				if (evaluatorType === EvaluatorType.Client) {
					// Tab 0 (client evals): search by talent name
					const talentName = `${e.freelancerFirstName || ""} ${e.freelancerLastName || ""}`.toLowerCase();
					return talentName.includes(search);
				} else {
					// Tab 1 (freelancer evals): search by evaluator name
					const evaluatorName = `${e.assignedToFirstName || ""} ${e.assignedToLastName || ""}`.toLowerCase();
					return evaluatorName.includes(search);
				}
			});
		}
		if (appliedFilters.projectName) {
			const search = appliedFilters.projectName.toLowerCase();
			items = items.filter(e => (e.projectName || "").toLowerCase().includes(search));
		}
		if (appliedFilters.projectNumber) {
			const search = appliedFilters.projectNumber.toLowerCase();
			items = items.filter(e => (e.projectNumber || "").toLowerCase().includes(search));
		}
		if (appliedFilters.companyName) {
			const search = appliedFilters.companyName.toLowerCase();
			items = items.filter(e => (e.evaluatorCompanyName || "").toLowerCase().includes(search));
		}
		if (appliedFilters.rating !== null) {
			items = items.filter(e => e.rating === appliedFilters.rating);
		}
		if (appliedFilters.consent !== null) {
			items = items.filter(e => e.consentGiven === appliedFilters.consent);
		}
		if (appliedFilters.status !== null) {
			items = items.filter(e => e.status === appliedFilters.status);
		}

		return items;
	}, [data?.items, appliedFilters]);

	// ── Group by project ───────────────────────────────────────

	const groupedEvaluations = useMemo(() => {
		const groups: Record<string, ProjectGroup> = {};
		for (const evaluation of filteredItems) {
			const key = `${evaluation.projectId}`;
			if (!groups[key]) {
				groups[key] = {
					key,
					companyName: evaluation.evaluatorCompanyName || "",
					projectNumber: evaluation.projectNumber || "",
					projectName: evaluation.projectName || "",
					evaluations: []
				};
			}
			groups[key].evaluations.push(evaluation);
		}

		let sortedGroups = Object.values(groups);
		const direction = currentSort.sortDirection === "Asc" ? 1 : -1;

		if (sortField === "title") {
			sortedGroups.sort((a, b) => direction * a.projectName.localeCompare(b.projectName));
		} else {
			sortedGroups.sort((a, b) => {
				const dateA = a.evaluations[0]?.submittedAt || "";
				const dateB = b.evaluations[0]?.submittedAt || "";
				return direction * dateA.localeCompare(dateB);
			});
		}

		return sortedGroups;
	}, [filteredItems, sortField, currentSort.sortDirection]);

	// ── Handlers ───────────────────────────────────────────────

	const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
		setActiveTab(newValue);
		setPage(1);
	};

	const handleSearch = () => {
		setAppliedFilters({
			personName: filterPersonName,
			projectName: filterProjectName,
			projectNumber: filterProjectNumber,
			companyName: filterCompany?.tradingName || filterCompany?.legalName || "",
			rating: filterRating,
			consent: filterConsent,
			status: filterStatus
		});
		setPage(1);
	};

	const handleResetFilters = () => {
		setFilterPersonName("");
		setFilterProjectName("");
		setFilterProjectNumber("");
		setFilterCompany(null);
		setFilterRating(null);
		setFilterConsent(null);
		setFilterStatus(null);
		setAppliedFilters({
			personName: "",
			projectName: "",
			projectNumber: "",
			companyName: "",
			rating: null,
			consent: null,
			status: null
		});
		setPage(1);
	};

	const handlePageSizeChange = (newPageSize: number) => {
		setPageSize(newPageSize);
		setPage(1);
	};

	const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, evaluation: ProjectEvaluation) => {
		setMenuAnchorEl(event.currentTarget);
		setMenuEvaluation(evaluation);
	};

	const handleMenuClose = () => {
		setMenuAnchorEl(null);
		setMenuEvaluation(null);
	};

	const handleModerateAction = (action: ProjectEvaluationStatus) => {
		if (!menuEvaluation) return;
		moderateMutation.mutate({
			id: menuEvaluation.id,
			request: { newStatus: action }
		});
		handleMenuClose();
	};

	const handleOpenEdit = () => {
		if (!menuEvaluation) return;
		setEditEvaluation(menuEvaluation);
		setEditRating(menuEvaluation.rating || 0);
		setEditComment(menuEvaluation.comment || "");
		setEditDialogOpen(true);
		handleMenuClose();
	};

	const handleConfirmEdit = () => {
		if (!editEvaluation) return;
		editMutation.mutate(
			{
				id: editEvaluation.id,
				request: {
					rating: editRating,
					comment: editComment
				}
			},
			{
				onSuccess: () => {
					setEditDialogOpen(false);
					setEditEvaluation(null);
				}
			}
		);
	};

	// ── Determine available menu actions for an evaluation ─────

	const canPublish = (e: ProjectEvaluation) =>
		e.status === ProjectEvaluationStatus.Submitted ||
		e.status === ProjectEvaluationStatus.Rejected;

	const canReject = (e: ProjectEvaluation) =>
		e.status === ProjectEvaluationStatus.Submitted;

	const canUnpublish = (e: ProjectEvaluation) =>
		e.status === ProjectEvaluationStatus.Published;

	const canEdit = (e: ProjectEvaluation) =>
		e.status === ProjectEvaluationStatus.Submitted ||
		e.status === ProjectEvaluationStatus.Rejected;

	// ── Render ─────────────────────────────────────────────────

	return (
		<Box sx={{ p: 3 }}>
			<Typography variant="h2" sx={{ mb: 3 }}>
				{t("evaluations.title")}
			</Typography>

			{/* ── Tabs ──────────────────────────────────────── */}
			<Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
				<Tabs
					value={activeTab}
					onChange={handleTabChange}
					sx={{
						"& .MuiTab-root": {
							borderRadius: "8px 8px 0 0",
							mr: 1,
							minHeight: 48,
							color: "text.primary",
							textTransform: "none"
						},
						"& .MuiTab-root.Mui-selected": {
							backgroundColor: zoullooColors.secondary.light + "20",
							color: `${zoullooColors.secondary.main} !important`
						},
						"& .MuiTabs-indicator": {
							backgroundColor: zoullooColors.secondary.main
						}
					}}
				>
					<Tab label={t("evaluations.tabs.clientEvaluations")} />
					<Tab label={t("evaluations.tabs.freelancerEvaluations")} />
				</Tabs>
			</Box>

			{/* ── Filters ───────────────────────────────────── */}
			<Paper
				sx={{
					p: 2.5,
					mb: 3,
					borderRadius: 4,
					"& .MuiInputBase-root": { height: 56, minHeight: 56 },
					"& .MuiAutocomplete-root .MuiInputBase-root": { height: 56, minHeight: 56 }
				}}
			>
				{/* Ligne 1 : champs texte + entreprise + bouton rechercher */}
				<Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
					<Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
						<TextField
							fullWidth
							size="small"
							label={activeTab === 0 ? t("evaluations.filters.talentName") : t("evaluations.filters.clientName")}
							value={filterPersonName}
							onChange={e => setFilterPersonName(e.target.value)}
						/>
					</Grid>
					<Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
						<TextField
							fullWidth
							size="small"
							label={t("evaluations.filters.projectName")}
							value={filterProjectName}
							onChange={e => setFilterProjectName(e.target.value)}
						/>
					</Grid>
					<Grid size={{ xs: 12, sm: 6, md: 2 }}>
						<TextField
							fullWidth
							size="small"
							label={t("evaluations.filters.projectNumber")}
							value={filterProjectNumber}
							onChange={e => setFilterProjectNumber(e.target.value)}
						/>
					</Grid>
					<Grid size={{ xs: 12, sm: 6, md: 3 }}>
						<CompanyAutocomplete
							value={filterCompany}
							onChange={setFilterCompany}
							size="small"
							label={t("evaluations.filters.company")}
						/>
					</Grid>
					<Grid size={{ xs: 12, sm: 6, md: 2 }}>
						<Button
							fullWidth
							variant="contained"
							startIcon={<SearchIcon />}
							onClick={handleSearch}
							sx={{
								backgroundColor: zoullooColors.secondary.main,
								"&:hover": { backgroundColor: zoullooColors.secondary.dark },
								textTransform: "none",
								height: 56
							}}
						>
							{t("evaluations.filters.search")}
						</Button>
					</Grid>
				</Grid>
				{/* Ligne 2 : filtres dropdown + réinitialiser aligné sous Rechercher */}
				<Grid container spacing={2} alignItems="center">
					<Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
						<Autocomplete
							options={statusOptions}
							getOptionLabel={option => option.label}
							value={statusOptions.find(o => o.value === filterStatus) || statusOptions[0]}
							onChange={(_, newValue) => setFilterStatus(newValue?.value ?? null)}
							disableClearable
							renderInput={params => (
								<TextField
									{...params}
									label={t("evaluations.filters.status")}
									size="small"
								/>
							)}
						/>
					</Grid>
					<Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
						<Autocomplete
							options={ratingOptions}
							getOptionLabel={option => option.label}
							value={ratingOptions.find(o => o.value === filterRating) || ratingOptions[0]}
							onChange={(_, newValue) => setFilterRating(newValue?.value ?? null)}
							disableClearable
							renderInput={params => (
								<TextField
									{...params}
									label={t("evaluations.filters.rating")}
									size="small"
								/>
							)}
						/>
					</Grid>
					<Grid size={{ xs: 12, sm: 6, md: 2 }}>
						<Autocomplete
							options={consentOptions}
							getOptionLabel={option => option.label}
							value={consentOptions.find(o => o.value === filterConsent) || consentOptions[0]}
							onChange={(_, newValue) => setFilterConsent(newValue?.value ?? null)}
							disableClearable
							renderInput={params => (
								<TextField
									{...params}
									label={t("evaluations.filters.consent")}
									size="small"
								/>
							)}
						/>
					</Grid>
					{/* Espaceur pour pousser le bouton à droite */}
					<Grid size={{ xs: 0, md: 3 }} sx={{ display: { xs: "none", md: "block" } }} />
					<Grid size={{ xs: 12, sm: 6, md: 2 }}>
						<Button
							fullWidth
							variant="outlined"
							startIcon={<RestartAltIcon />}
							onClick={handleResetFilters}
							sx={{
								borderColor: zoullooColors.secondary.main,
								color: zoullooColors.secondary.main,
								"&:hover": {
									borderColor: zoullooColors.secondary.dark,
									backgroundColor: zoullooColors.secondary.light + "10"
								},
								textTransform: "none",
								height: 56
							}}
						>
							{t("evaluations.filters.reset")}
						</Button>
					</Grid>
				</Grid>
			</Paper>

			{/* ── Results ───────────────────────────────────── */}
			{isLoading ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
					<CircularProgress />
				</Box>
			) : !data || filteredItems.length === 0 ? (
				<Paper sx={{ p: 4, textAlign: "center", borderRadius: 4 }}>
					<Typography color="text.secondary">
						{t("evaluations.noResults")}
					</Typography>
				</Paper>
			) : (
				<>
					{/* Grouped evaluations */}
					{groupedEvaluations.map(group => (
						<Paper
							key={group.key}
							sx={{
								mb: 3,
								borderRadius: 4,
								border: `1px solid ${zoullooColors.grey[200]}`,
								overflow: "hidden"
							}}
						>
							{/* Group header */}
							<Box sx={{ p: 2.5, pb: 1.5 }}>
								<Typography
									variant="h6"
									sx={{
										fontWeight: 600,
										color: zoullooColors.secondary.main
									}}
								>
									{group.companyName} - {group.projectNumber} :{" "}
									<Typography
										component="span"
										variant="h6"
										onClick={() => navigateByEnvironment(`/projects/${group.key}`)}
										sx={{
											fontWeight: 600,
											color: zoullooColors.secondary.main,
											textDecoration: "none",
											cursor: "pointer",
											"&:hover": {
												textDecoration: "underline"
											}
										}}
									>
										{group.projectName}
									</Typography>
								</Typography>
							</Box>

							{/* Evaluation cards */}
							{group.evaluations.map((evaluation, index) => (
								<Box key={evaluation.id}>
									{index > 0 && <Divider />}
									<Box sx={{ p: 2.5, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
										{/* Left content */}
										<Box sx={{ flex: 1, mr: 2 }}>
											{/* Star rating + status + date row */}
											<Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
												{evaluation.rating && (
													<StarRating
														value={evaluation.rating}
														readOnly
														size="small"
													/>
												)}
												<Chip
													label={getStatusLabel(evaluation.status)}
													size="small"
													sx={{
														backgroundColor: getStatusColor(evaluation.status) + "15",
														color: getStatusColor(evaluation.status),
														fontWeight: 600
													}}
												/>
												<Typography variant="body2" color="text.secondary">
													{formatDate(evaluation.submittedAt)}
												</Typography>
											</Box>

											{/* Evaluator / Talent name */}
											{activeTab === 0 ? (
												<Typography variant="subtitle2" sx={{ mb: 0.5 }}>
													{`${evaluation.evaluatedByFirstName || evaluation.evaluatorFirstName || ""} ${evaluation.evaluatedByLastName || evaluation.evaluatorLastName || ""}`.trim()}
													{evaluation.assignedToJobTitle && (
														<Typography
															component="span"
															sx={{ fontSize: "inherit", fontWeight: "normal" }}
															color="text.secondary"
														>
															, {evaluation.assignedToJobTitle}
														</Typography>
													)}
												</Typography>
											) : (
												<Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
													{`${evaluation.freelancerFirstName || ""} ${evaluation.freelancerLastName || ""}`.trim()}
												</Typography>
											)}

											{/* Comment */}
											{evaluation.comment && (
												<Box sx={{ mt: 1 }}>
													<RichTextReadOnly content={evaluation.comment} extensions={getExtensions()} />
												</Box>
											)}

											{/* Consent display - only show when given */}
											{evaluation.consentGiven && (
												<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1 }}>
													<CheckIcon sx={{ fontSize: "0.9rem", color: zoullooColors.success.main }} />
													<Typography variant="caption" sx={{ color: zoullooColors.success.main }}>
														{t("evaluations.consent")}
													</Typography>
												</Box>
											)}
										</Box>

										{/* Right: dropdown button */}
										<Box sx={{ flexShrink: 0 }}>
											<Button
												variant="outlined"
												size="small"
												endIcon={<ArrowDropDownIcon />}
												onClick={e => handleMenuOpen(e, evaluation)}
												sx={{
													textTransform: "none",
													borderColor: zoullooColors.secondary.main,
													color: zoullooColors.secondary.main,
													"&:hover": {
														borderColor: zoullooColors.secondary.dark,
														backgroundColor: zoullooColors.secondary.main + "08"
													}
												}}
											>
												{t("evaluations.selectAction")}
											</Button>
										</Box>
									</Box>
								</Box>
							))}
						</Paper>
					))}

					{/* ── Pagination ────────────────────────────── */}
					<TablePagination
						currentPage={page}
						pageSize={pageSize}
						totalCount={data?.totalCount || 0}
						totalPages={data?.totalPages || 1}
						onPageChange={setPage}
						onPageSizeChange={handlePageSizeChange}
						pageSizeOptions={[10, 20, 50]}
						sortOptions={sortOptions}
						currentSort={currentSort}
						onSortChange={handleSortChange}
						sticky={false}
						mx={0}
					/>
				</>
			)}

			{/* ── Moderation dropdown menu ──────────────────── */}
			<Menu
				anchorEl={menuAnchorEl}
				open={Boolean(menuAnchorEl)}
				onClose={handleMenuClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
			>
				{menuEvaluation && canPublish(menuEvaluation) && (
					<MenuItem
						onClick={() => handleModerateAction(ProjectEvaluationStatus.Published)}
						sx={{ color: zoullooColors.success.main }}
					>
						{t("evaluations.actions.publish")}
					</MenuItem>
				)}
				{menuEvaluation && canReject(menuEvaluation) && (
					<MenuItem
						onClick={() => handleModerateAction(ProjectEvaluationStatus.Rejected)}
						sx={{ color: zoullooColors.error.main }}
					>
						{t("evaluations.actions.reject")}
					</MenuItem>
				)}
				{menuEvaluation && canUnpublish(menuEvaluation) && (
					<MenuItem
						onClick={() => handleModerateAction(ProjectEvaluationStatus.Submitted)}
						sx={{ color: zoullooColors.warning.main }}
					>
						{t("evaluations.actions.unpublish")}
					</MenuItem>
				)}
				{menuEvaluation && canEdit(menuEvaluation) && (
					<MenuItem
						onClick={handleOpenEdit}
						sx={{ color: zoullooColors.secondary.main }}
					>
						{t("evaluations.actions.edit")}
					</MenuItem>
				)}
			</Menu>

			{/* ── Edit Dialog ───────────────────────────────── */}
			<EvaluationEditDialog
				open={editDialogOpen}
				rating={editRating}
				comment={editComment}
				isPending={editMutation.isPending}
				onRatingChange={setEditRating}
				onCommentChange={setEditComment}
				onConfirm={handleConfirmEdit}
				onClose={() => setEditDialogOpen(false)}
			/>
		</Box>
	);
};

export default Evaluations;
